import React from "react";
import { Battery, Wrench, KeyRound, CircleDot, Fuel, ShieldCheck, ChevronRight } from "lucide-react";
import { CONTACTS, waLink } from "../lib/contact";

const services = [
  {
    icon: Battery,
    title: "Carga de Bateria",
    desc: "Seu carro não liga? Realizamos carga de bateria e auxílio de partida no local, com rapidez e segurança.",
    tag: "Emergencial",
    image:
      "https://customer-assets.emergentagent.com/job_auto-socorro-rapido/artifacts/bzqsxjxy_images.jpeg",
  },
  {
    icon: CircleDot,
    title: "Troca de Estepe",
    desc: "Pneu furado? Fazemos a troca do estepe onde você estiver, evitando riscos e atrasos.",
    tag: "Rua / Estrada",
    image:
      "https://customer-assets.emergentagent.com/job_auto-socorro-rapido/artifacts/hu92dn3f_dinamicar-pneus-troca-pneu.jpg",
  },
  {
    icon: KeyRound,
    title: "Chaveiro Automotivo",
    desc: "Abertura de portas e auxílio quando a chave fica trancada dentro do veículo, sem danos.",
    tag: "Abertura",
    image:
      "https://images.pexels.com/photos/97079/pexels-photo-97079.jpeg?auto=compress&cs=tinysrgb&w=900&h=600&fit=crop",
  },
  {
    icon: Wrench,
    title: "Borracharia Móvel",
    desc: "Atendimento emergencial para problemas com pneus, sem precisar deslocar o veículo.",
    tag: "Móvel",
    image:
      "https://customer-assets.emergentagent.com/job_auto-socorro-rapido/artifacts/x6pxfiku_Mobile%20Tire%20Repair%20Services%20for%20Commercial%20Trucks%201.jpg",
  },
  {
    icon: Fuel,
    title: "Auxílio Combustível",
    desc: "Ficou sem gasolina, etanol ou diesel na rua? Levamos combustível até você para você seguir viagem.",
    tag: "Pane Seca",
    image:
      "https://images.pexels.com/photos/9799735/pexels-photo-9799735.jpeg?auto=compress&cs=tinysrgb&w=900&h=600&fit=crop",
  },
  {
    icon: ShieldCheck,
    title: "Anjo da Guarda",
    desc: "Acompanhamento e escolta para motoristas em situações de vulnerabilidade — retorno seguro para casa a qualquer hora.",
    tag: "Segurança 24h",
    image:
      "https://images.pexels.com/photos/136794/pexels-photo-136794.jpeg?auto=compress&cs=tinysrgb&w=900&h=600&fit=crop",
  },
];

export default function Services() {
  return (
    <section
      id="servicos"
      data-testid="services-section"
      className="relative py-24 lg:py-32 bg-black"
    >
      <div className="absolute inset-0 speed-lines opacity-50"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section header */}
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#f3bd0e]/40 rounded-sm mb-5">
            <span className="w-1.5 h-1.5 bg-[#f3bd0e]"></span>
            <span className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#f3bd0e]">
              O que fazemos
            </span>
          </div>
          <h2
            className="font-display text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight text-white"
            data-testid="services-title"
          >
            Nossos <span className="text-[#f3bd0e]">Serviços</span>
          </h2>
          <p className="mt-5 text-white/70 text-base sm:text-lg max-w-2xl">
            Atendimento emergencial completo para que você volte à estrada o
            mais rápido possível — onde estiver, a qualquer hora.
          </p>
        </div>

        {/* Grid */}
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((s, idx) => {
            const Icon = s.icon;
            return (
              <article
                key={s.title}
                data-testid={`service-card-${idx}`}
                className="group relative bg-[#0A0A0A] border border-white/10 rounded-lg transition-all duration-300 hover:border-[#f3bd0e] hover:-translate-y-1 hover:shadow-[0_0_30px_rgba(243,189,14,0.18)] overflow-hidden flex flex-col"
              >
                <div className="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#f3bd0e] to-transparent opacity-0 group-hover:opacity-100 transition z-10"></div>

                {/* Image */}
                <div className="relative h-44 sm:h-48 overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.title}
                    loading="lazy"
                    data-testid={`service-image-${idx}`}
                    className="absolute inset-0 w-full h-full object-cover grayscale-[40%] group-hover:grayscale-0 group-hover:scale-110 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent"></div>
                  <div className="absolute top-3 left-3">
                    <div className="w-11 h-11 rounded-md bg-[#f3bd0e] flex items-center justify-center shadow-[0_0_20px_rgba(243,189,14,0.4)] group-hover:rotate-[-8deg] transition-transform duration-300">
                      <Icon className="w-6 h-6 text-black" strokeWidth={2.4} />
                    </div>
                  </div>
                  <div className="absolute top-4 right-4 font-display text-3xl text-white/30 group-hover:text-[#f3bd0e] transition">
                    0{idx + 1}
                  </div>
                </div>

                {/* Body */}
                <div className="relative p-6 flex-1 flex flex-col">
                  <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#f3bd0e]/80 mb-2">
                    {s.tag}
                  </div>
                  <h3 className="font-display text-lg sm:text-xl uppercase text-white tracking-tight">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm text-white/65 leading-relaxed flex-1">
                    {s.desc}
                  </p>

                  <a
                    href={waLink(
                      CONTACTS.phones[0].raw,
                      `Olá! Preciso de ${s.title.toLowerCase()}. Pode me ajudar?`
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`service-cta-${idx}`}
                    className="mt-6 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-white/80 group-hover:text-[#f3bd0e] transition"
                  >
                    Solicitar agora
                    <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition" />
                  </a>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
